import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LiveSearch } from "@/components/LiveSearch";
import { ListingCard } from "@/components/ListingCard";
import { lazy, Suspense } from "react";
const ListingDrawer = lazy(() => import("@/components/ListingDrawer").then(m => ({ default: m.ListingDrawer })));
import { CategoryGridSkeleton, ListingGridSkeleton } from "@/components/Skeleton";
import { useSiteContent } from "@/context/SiteContentContext";
import { useAuth } from "@/context/AuthContext";
import { SEO, generateOrganizationSchema } from "@/components/SEO";
import PromoSubscribeBanner from "@/components/PromoSubscribeBanner";
import { HomepageBanner } from "@/components/HomepageBanner";
import { DiscoverCarousel } from "@/components/DiscoverCarousel";
import { getResponsiveSrc, handleImageError } from "@/utils/responsiveImage";
import { 
  Sparkles, 
  Map, 
  Utensils, 
  HeartPulse, 
  Bus, 
  Briefcase, 
  Package, 
  Gift, 
  Shield, 
  Star,
  ArrowRight,
  ChevronRight,
  Bookmark,
  StickyNote,
  CalendarDays,
  Folder,
  ClipboardList,
  Heart,
  UserPlus,
  LogIn,
  Search,
  User,
  ChevronDown,
  Grid3X3,
  List,
  MapPin,
  Music,
  Compass,
  ChefHat,
  ShieldPlus,
  Boxes,
  Shirt,
  Handshake,
  Award
} from "lucide-react";

import { BACKEND_URL } from "@/config/runtime";

const iconMap = {
  sparkles: Sparkles,
  map: Map,
  utensils: Utensils,
  "heart-pulse": HeartPulse,
  bus: Bus,
  briefcase: Briefcase,
  package: Package,
  gift: Gift,
  shield: Shield,
  star: Star,
  music: Music,
  compass: Compass,
  "chef-hat": ChefHat,
  "shield-plus": ShieldPlus,
  "clipboard-list": ClipboardList,
  boxes: Boxes,
  shirt: Shirt,
  handshake: Handshake,
  award: Award,
  settings: Briefcase,
};

const CategoryCard = ({ category, index }) => {
  const Icon = iconMap[category.icon] || Star;
  
  return (
    <Link
      to={`/category/${category.slug}`}
      className={`category-card group relative h-48 md:h-56 overflow-hidden rounded-2xl opacity-0 animate-fade-in-up stagger-${index + 1}`}
      data-testid={`category-card-${category.slug}`}
    >
      <img
        src={getResponsiveSrc(category.image_url)}
        onError={(e) => handleImageError(e, category.image_url)}
        alt={category.name}
        className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
        referrerPolicy="no-referrer"
        loading="lazy"
        sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 18vw"
        width={350}
        height={200}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10" />
      <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-8 h-8 rounded-lg bg-[#3D6B4A] flex items-center justify-center">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h3 className="font-bold text-white text-lg">{category.name}</h3>
        </div>
        <p className="text-white/80 text-sm line-clamp-1">{category.description}</p>
      </div>
      <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
          <ChevronRight className="w-4 h-4 text-white" />
        </div>
      </div>
    </Link>
  );
};

// Compact list item for categories - used in list view mode
const CategoryListItem = ({ category }) => {
  const Icon = iconMap[category.icon] || Star;
  
  return (
    <Link
      to={`/category/${category.slug}`}
      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-stone-200 hover:border-[#3D6B4A] hover:shadow-sm transition-all"
      data-testid={`category-list-item-${category.slug}`}
    >
      {/* Thumbnail */}
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0 relative">
        <img
          src={category.image_url}
          alt={category.name}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white drop-shadow" />
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-[#1E3A2F] text-sm sm:text-base line-clamp-1">
          {category.name}
        </h3>
        <p className="text-xs sm:text-sm text-[#2D5A3D] line-clamp-1">
          {category.description}
        </p>
      </div>
      
      {/* Arrow */}
      <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
    </Link>
  );
};

// Icon-based compact category tile
const CategoryIconCard = ({ category, index }) => {
  const Icon = iconMap[category.icon] || Star;
  return (
    <Link
      to={`/category/${category.slug}`}
      className={`group flex flex-col items-center gap-2 p-4 w-24 sm:w-28 rounded-xl hover:bg-white/15 transition-all opacity-0 animate-fade-in-up stagger-${index + 1}`}
      data-testid={`category-icon-${category.slug}`}
    >
      <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm group-hover:bg-white/30 flex items-center justify-center transition-colors">
        <Icon className="w-7 h-7 text-white" />
      </div>
      <span className="text-xs sm:text-sm font-medium text-white text-center leading-tight line-clamp-2 drop-shadow">
        {category.name}
      </span>
    </Link>
  );
};


// Compact list item for promoted listings - used in list view mode
const PromotedListItem = ({ listing, onQuickView }) => {
  const displayImage = listing.flyer_image || listing.gallery_images?.[0];
  
  const getLocation = () => {
    if (listing.service_areas?.some(area => area.toLowerCase() === 'anywhere') || 
        (!listing.locations?.length && !listing.service_areas?.length)) {
      return "Anywhere";
    }
    if (listing.service_areas?.length > 1) return "Multiple";
    if (listing.service_areas?.[0]) return listing.service_areas[0];
    if (listing.locations?.[0]?.city) return listing.locations[0].city;
    return "Anywhere";
  };

  return (
    <div 
      onClick={() => onQuickView(listing.slug)}
      className="flex items-center gap-3 p-3 bg-white rounded-xl border border-stone-200 hover:border-[#3D6B4A] hover:shadow-sm transition-all cursor-pointer"
      data-testid={`promoted-list-item-${listing.listing_id}`}
    >
      {/* Thumbnail */}
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-stone-100 flex-shrink-0">
        {displayImage ? (
          <img 
            src={displayImage} 
            alt={listing.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-400 text-lg font-bold">
            {listing.title?.charAt(0) || 'V'}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-[#1E3A2F] text-sm sm:text-base line-clamp-1">
            {listing.title}
          </h3>
          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex-shrink-0">⭐</span>
        </div>
        <p className="text-xs sm:text-sm text-[#2D5A3D] line-clamp-1">
          {listing.description}
        </p>
        <div className="flex items-center gap-1 mt-1 text-xs text-stone-500">
          <MapPin className="w-3 h-3" />
          {getLocation()}
        </div>
      </div>
      
      {/* Arrow */}
      <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
    </div>
  );
};

// Default hero background image URL - optimized WebP version (~105KB vs original 6MB)
const DEFAULT_HERO_IMAGE_URL = "https://campyid-ver1.s3.us-east-2.amazonaws.com/images/hero/campyid_hero.webp";

// Check if user is on mobile/slow connection
const isMobileOrSlowConnection = () => {
  if (typeof navigator !== 'undefined' && navigator.connection) {
    const conn = navigator.connection;
    return conn.saveData || conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g';
  }
  return window.innerWidth < 768;
};

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [promotedListings, setPromotedListings] = useState([]);
  const [visiblePromotedIndex, setVisiblePromotedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { getContent, content: siteContent, loading: contentLoading } = useSiteContent();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const isVendor = user?.account_type === "vendor";
  const isCamp = user?.account_type === "camp";
  
  // View mode from localStorage (shared with SearchPage)
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('searchViewMode') || 'grid';
    }
    return 'grid';
  });
  
  // Toggle view mode and save to localStorage
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('searchViewMode', mode);
  };
  
  // Check if vendor page/button is enabled (for "I'm a Vendor" button)
  const vendorPageEnabled = getContent("vendor_page_enabled", "true") === "true";
  // Check if vendor CTA section is enabled (separate toggle)
  const vendorCtaEnabled = getContent("home_vendor_cta_enabled", "true") === "true";
  
  // Use site content hero image if available, otherwise default
  const heroImageUrl = siteContent?.home_hero_image || DEFAULT_HERO_IMAGE_URL;
  
  // Drawer state for quick view
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedListingSlug, setSelectedListingSlug] = useState(null);
  
  // Cached data for ListingCard to avoid N API calls
  const [savedListingsCache, setSavedListingsCache] = useState(null);
  const [vendorListsCache, setVendorListsCache] = useState(null);

  // Camp Dashboard features for promotion - using editable content
  const campFeatures = [
    { icon: Bookmark, titleKey: "home_feature_save", descKey: "home_feature_save_desc", defaultTitle: "Save Vendors", defaultDesc: "Bookmark vendors you're interested in for easy access" },
    { icon: StickyNote, titleKey: "home_feature_notes", descKey: "home_feature_notes_desc", defaultTitle: "Add Notes", defaultDesc: "Keep track of conversations and important details" },
    { icon: CalendarDays, titleKey: "home_feature_schedule", descKey: "home_feature_schedule_desc", defaultTitle: "Schedule & Book", defaultDesc: "Set dates and times for vendor visits" },
    { icon: Folder, titleKey: "home_feature_lists", descKey: "home_feature_lists_desc", defaultTitle: "Organize with Lists", defaultDesc: "Create custom lists to categorize your vendors" },
    { icon: ClipboardList, titleKey: "home_feature_status", descKey: "home_feature_status_desc", defaultTitle: "Track Status", defaultDesc: "Mark vendors as Probable, Booked, or Custom statuses" },
    { icon: Star, titleKey: "home_feature_calendar", descKey: "home_feature_calendar_desc", defaultTitle: "Camp Calendar", defaultDesc: "View all your bookings in a beautiful calendar view" },
  ];

  const handleQuickView = (slug) => {
    setSelectedListingSlug(slug);
    setDrawerOpen(true);
  };
  
  // Fetch saved listings and vendor lists once for all cards (performance optimization)
  useEffect(() => {
    if (!isAuthenticated) {
      setSavedListingsCache([]);
      setVendorListsCache([]);
      return;
    }
    
    const fetchCachedData = async () => {
      try {
        const [savedRes, listsRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/saved-listings`, { credentials: "include" }),
          fetch(`${BACKEND_URL}/api/camp/lists`, { credentials: "include" })
        ]);
        
        if (savedRes.ok) {
          setSavedListingsCache(await savedRes.json());
        }
        if (listsRes.ok) {
          setVendorListsCache(await listsRes.json());
        }
      } catch (error) {
        console.error("Error fetching cached data:", error);
      }
    };
    
    fetchCachedData();
  }, [isAuthenticated]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories and promoted listings in parallel
        const [catRes, listRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/categories`),
          fetch(`${BACKEND_URL}/api/listings/promoted-homepage`)
        ]);
        
        const catData = await catRes.json();
        setCategories(catData);

        const listData = await listRes.json();
        setPromotedListings(listData.listings || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Rotate promoted listings every 10 seconds (show 8 at a time - 2 rows of 4)
  useEffect(() => {
    if (promotedListings.length <= 8) return; // No need to rotate if 8 or fewer
    
    const rotationInterval = setInterval(() => {
      setVisiblePromotedIndex(prev => {
        const nextIndex = prev + 8;
        // If we've gone past the end, loop back to start
        return nextIndex >= promotedListings.length ? 0 : nextIndex;
      });
    }, 10000); // 10 seconds
    
    return () => clearInterval(rotationInterval);
  }, [promotedListings.length]);

  // Get the currently visible promoted listings (8 at a time with wraparound)
  const getVisiblePromotedListings = () => {
    if (promotedListings.length === 0) return [];
    if (promotedListings.length <= 8) return promotedListings;
    
    const visible = [];
    for (let i = 0; i < 8; i++) {
      const index = (visiblePromotedIndex + i) % promotedListings.length;
      visible.push(promotedListings[index]);
    }
    return visible;
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Camp Vendor Directory — Find Trusted Vendors for Your Summer Camp"
        description="CampYid is the #1 Jewish camp vendor directory. Find kosher catering, entertainment, transportation, trips, supplies, and more for your summer camp. Browse trusted vendors by category."
        url="/"
        structuredData={generateOrganizationSchema()}
      />
      
      {/* Fixed Hero Background Image */}
      <div 
        className="fixed inset-0 w-full h-screen"
        style={{ zIndex: 0 }}
      >
        <img
          src={heroImageUrl}
          alt=""
          className="w-full h-full object-cover"
          fetchpriority="high"
          loading="eager"
          decoding="async"
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
      </div>

      {/* Homepage Banner — site-wide, admin-managed, sits between nav and hero */}
      <div className="relative" style={{ zIndex: 2 }}>
        <HomepageBanner
          siteContent={siteContent}
          onOpenListing={(slug) => {
            setSelectedListingSlug(slug);
            setDrawerOpen(true);
          }}
        />
      </div>

      {/* Hero Section - positioned over fixed background */}
      <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-center py-16 md:py-24" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
              {getContent("home_hero_title", "Plan Smarter Camp Better").split("Camp").map((part, i) => 
                i === 0 ? part : <span key={`hero-title-${i}`}><span className="text-[#FFD93D] drop-shadow-md">Camp</span>{part}</span>
              )}
            </h1>
            <p className="text-lg md:text-xl text-white/95 mb-8 leading-relaxed drop-shadow">
              {getContent("home_hero_subtitle", "Discover top vendors for entertainment, catering, transportation, and more. Save time planning your perfect summer camp experience.")}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto hero-search-container">
              <LiveSearch 
                placeholder={getContent("home_search_placeholder", "Search vendors, services, or categories...")}
                size="large"
                className="w-full"
                isHeroSearch={true}
              />
              <p className="text-white/70 text-sm mt-3">
                {getContent("home_search_hint", 'Try searching: "magician", "catering", "transportation"')}
              </p>
            </div>

            {/* Mini spinning card carousel — fixed height to prevent CLS */}
            {getContent("home_discover_carousel_enabled", "true") === "true" && (
              <div className="mt-5 mb-2" style={{ minHeight: "120px" }}>
                <DiscoverCarousel />
              </div>
            )}

            {/* CTA Pill Buttons - hide for authenticated camp directors only */}
            {!isCamp && (
              <div className="flex flex-row gap-2 sm:gap-4 mt-6 sm:mt-8 justify-center px-2">
                {vendorPageEnabled && (
                <Link to="/for-vendors" data-testid="cta-vendor-btn" className="flex-1 sm:flex-none sm:w-[260px]"
                  onClick={() => { fetch(`${BACKEND_URL}/api/analytics/cta-click`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ cta_type: "vendor" }) }).catch(() => {}); }}
                >
                  <Button 
                    size="lg"
                    className="cta-button w-full bg-white/95 hover:bg-white text-[#1E3A2F] font-semibold px-3 py-3 sm:px-8 sm:py-6 text-sm sm:text-lg rounded-full shadow-lg border-2 border-white/50"
                  >
                    <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">I'm a Vendor</span>
                    <span className="sm:hidden">Vendor</span>
                  </Button>
                </Link>
                )}
                <Link to="/for-directors" data-testid="cta-director-btn" className="flex-1 sm:flex-none sm:w-[260px]"
                  onClick={() => { fetch(`${BACKEND_URL}/api/analytics/cta-click`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ cta_type: "director" }) }).catch(() => {}); }}
                >
                  <Button 
                    size="lg"
                    className="cta-button w-full bg-white/95 hover:bg-white text-[#1E3A2F] font-semibold px-3 py-3 sm:px-8 sm:py-6 text-sm sm:text-lg rounded-full shadow-lg border-2 border-white/50"
                  >
                    <User className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">I'm a Camp Director</span>
                    <span className="sm:hidden">Director</span>
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
        
        {/* Scroll Arrow */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="scroll-arrow text-white/80">
            <ChevronDown className="w-8 h-8" />
          </div>
        </div>
      </section>

      {/* Content Section - scrolls over the fixed background */}
      <div className={`relative ${getContent("home_category_display", "photo") === "icons" ? "" : "bg-white"}`} style={{ zIndex: 2 }}>
      
      {/* Categories Section */}
      <section className={`pt-6 pb-16 md:pt-8 md:pb-24 px-4 sm:px-6 lg:px-8 ${getContent("home_category_display", "photo") === "icons" ? "bg-[#1E3A2F]/60 backdrop-blur-sm" : ""}`}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6 flex flex-row items-stretch justify-center gap-2 sm:gap-3 max-w-3xl mx-auto">
            <Link to="/search" data-testid="browse-all-vendors-btn" className="flex-1 min-w-0">
              <Button size="lg" className={`w-full rounded-full px-3 sm:px-10 h-12 sm:h-14 text-sm sm:text-lg font-bold ${getContent("home_category_display", "photo") === "icons" ? "bg-white text-[#1E3A2F] hover:bg-white/90" : "bg-[#1E3A2F] text-white hover:bg-[#2D5A3D]"}`}>
                <span className="truncate">{getContent("home_browse_all_vendors", "Browse All Vendors")}</span>
              </Button>
            </Link>
            {getContent("home_trip_connect_enabled", "false") === "true" && (!isAuthenticated || user?.user_type !== "vendor") && (
              <Link to="/trips" data-testid="trip-connect-btn" className="flex-1 min-w-0">
                <Button size="lg" className={`w-full rounded-full px-3 sm:px-10 h-12 sm:h-14 text-sm sm:text-lg font-bold ${getContent("home_category_display", "photo") === "icons" ? "bg-[#FFD93D] text-[#1E3A2F] hover:bg-[#FFE566]" : "bg-[#FFD93D] text-[#1E3A2F] hover:bg-[#FFE566]"}`}>
                  <span className="truncate">{getContent("home_trip_connect_cta", "Trip Connect")}</span>
                </Button>
              </Link>
            )}
            {getContent("home_deals_cta_enabled", "true") === "true" && (
              <Link to="/search?deals=true" data-testid="browse-deals-btn" className="flex-1 min-w-0">
                <Button size="lg" className={`w-full rounded-full px-3 sm:px-10 h-12 sm:h-14 text-sm sm:text-lg font-bold ${getContent("home_category_display", "photo") === "icons" ? "bg-[#22C55E] text-white hover:bg-[#16A34A]" : "bg-[#22C55E] text-white hover:bg-[#16A34A]"}`}>
                  <span className="truncate">{getContent("home_deals_cta", "Browse All Deals")}</span>
                </Button>
              </Link>
            )}
          </div>
          <div className="mb-6">
              <h2 className={`text-3xl md:text-4xl font-bold mb-2 ${getContent("home_category_display", "photo") === "icons" ? "text-white drop-shadow-lg" : "text-[#1E3A2F]"}`}>
                {getContent("home_categories_title", "Browse by Category")}
              </h2>
              <p className={getContent("home_category_display", "photo") === "icons" ? "text-white/80 drop-shadow" : "text-[#2D5A3D]"}>{getContent("home_categories_subtitle", "Find the perfect vendors for every aspect of your camp")}</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={`category-skeleton-${i}`} className="h-48 md:h-56 rounded-2xl skeleton" />
              ))}
            </div>
          ) : getContent("home_category_display", "photo") === "icons" ? (
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category, index) => (
                <CategoryIconCard key={category.category_id} category={category} index={index} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {categories.map((category, index) => (
                <CategoryCard key={category.category_id} category={category} index={index} />
              ))}
            </div>
          )}
        </div>
        
        {/* Scroll Arrow */}
        <div className="flex justify-center mt-12">
          <div className="scroll-arrow text-[#2D5A3D]/60">
            <ChevronDown className="w-8 h-8" />
          </div>
        </div>
      </section>
      </div> {/* End categories wrapper */}

      {/* Are You a Camp Vendor? Section - Transparent over hero - Has its own toggle */}
      {vendorCtaEnabled && (
      <div className="relative py-16 md:py-24 px-4 sm:px-6 lg:px-8" style={{ zIndex: 2 }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">
            {getContent("home_cta_title", "Are You a Camp Vendor?")}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto drop-shadow">
            {getContent("home_cta_subtitle", "Join hundreds of vendors already reaching camp directors. Create your profile today and start getting inquiries!")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button className="bg-white text-[#1E3A2F] hover:bg-[#F5F0E6] px-8 py-4 text-lg rounded-full font-semibold" data-testid="vendor-cta-btn">
                {getContent("home_cta_button", "Create Your Profile")}
              </Button>
            </Link>
            <Link to="/search">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg rounded-full font-semibold" data-testid="browse-cta-btn">
                {getContent("home_browse_listings_btn", "Browse Vendors")}
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Scroll Arrow */}
        <div className="flex justify-center mt-12">
          <div className="scroll-arrow text-white/60">
            <ChevronDown className="w-8 h-8" />
          </div>
        </div>
      </div>
      )}

      {/* Second content section */}
      <div className="relative bg-white" style={{ zIndex: 2 }}>
      {/* Promoted Listings Section */}
      {promotedListings.length > 0 && (
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1E3A2F] mb-2">
                  {getContent("home_promoted_title", "Promoted Vendors")}
                </h2>
                <p className="text-[#2D5A3D]">
                  {getContent("home_promoted_subtitle", "Discover these top service providers")}
                  {promotedListings.length > 8 && (
                    <span className="ml-2 text-sm text-[#6B8E6B]">
                      (Showing {Math.min(8, promotedListings.length)} of {promotedListings.length})
                    </span>
                  )}
                </p>
              </div>
              <Link
                to="/search?promoted=true"
                className="flex items-center gap-2 text-[#2D5A3D] font-semibold hover:gap-3 transition-all mt-4 md:mt-0"
                data-testid="view-all-promoted"
              >
                {getContent("home_view_all_promoted", "View All")}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 transition-opacity duration-500">
              {getVisiblePromotedListings().map((listing) => (
                <ListingCard 
                  key={listing.listing_id} 
                  listing={listing} 
                  variant="promoted" 
                  onQuickView={handleQuickView}
                  savedListings={savedListingsCache}
                  vendorListsCache={vendorListsCache}
                />
              ))}
            </div>
            
            {/* Rotation indicator dots - only show if more than 8 listings */}
            {promotedListings.length > 8 && (
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: Math.ceil(promotedListings.length / 8) }).map((_, i) => (
                  <button
                    key={`promoted-dot-${i}`}
                    onClick={() => setVisiblePromotedIndex(i * 8)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      Math.floor(visiblePromotedIndex / 8) === i 
                        ? 'bg-[#2D5A3D] w-4' 
                        : 'bg-[#2D5A3D]/30 hover:bg-[#2D5A3D]/50'
                    }`}
                    aria-label={`Show promoted listings group ${i + 1}`}
                  />
                ))}
              </div>
            )}
            
            {/* Scroll Arrow */}
            <div className="flex justify-center mt-12">
              <div className="scroll-arrow text-[#2D5A3D]/60">
                <ChevronDown className="w-8 h-8" />
              </div>
            </div>
          </div>
        </section>
      )}
      </div> {/* End of scrolling content wrapper */}

      {/* Camp Director Dashboard Promotion - Only show when not signed in */}
      {!isAuthenticated && (
        <>

          {/* Camp Dashboard Promotion Section */}
          <div id="camp-director-section" className="relative" style={{ zIndex: 2 }}>
            {/* Hero Section - Fully transparent to show fixed background */}
            <div className="relative">
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                <div className="text-center max-w-3xl mx-auto">
                  <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
                    {getContent("home_camp_dashboard_title", "Camp Director Dashboard")}
                  </h2>
                  <p className="text-lg md:text-xl text-white/90 mb-8 drop-shadow">
                    {getContent("home_camp_dashboard_subtitle", "The ultimate tool for camp directors to find, organize, and book vendors for your summer camp season")}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg" 
                      className="bg-white text-[#1E3A2F] hover:bg-[#F5F0E6] font-semibold px-8 rounded-full"
                      onClick={() => navigate('/register')}
                      data-testid="camp-signup-btn"
                    >
                      <UserPlus className="w-5 h-5 mr-2" />
                      {getContent("home_camp_signup_btn", "Sign Up as Camp Director")}
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 rounded-full"
                      onClick={() => navigate('/login')}
                      data-testid="camp-signin-btn"
                    >
                      <LogIn className="w-5 h-5 mr-2" />
                      {getContent("home_camp_signin_btn", "Sign In")}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Scroll Arrow */}
              <div className="flex justify-center mt-8">
                <div className="scroll-arrow text-white/60">
                  <ChevronDown className="w-8 h-8" />
                </div>
              </div>
            </div>

            {/* Features Section */}
            <div className="bg-white py-8 md:py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-6 md:mb-12">
                  <h3 className="text-xl md:text-3xl font-bold text-[#1E3A2F] mb-2 md:mb-3">
                    {getContent("home_features_title", "Plan Smarter Camp Better")}
                  </h3>
                  <p className="text-[#2D5A3D] text-sm md:text-lg">
                    {getContent("home_features_subtitle", "Sign in as a Camp Director to access these powerful features")}
                  </p>
                </div>

                <div className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-6">
                  {campFeatures.map((feature) => (
                    <div 
                      key={feature.titleKey}
                      className="bg-white rounded-xl md:rounded-2xl p-2 md:p-6 border border-stone-200 hover:shadow-lg hover:border-[#6B8E6B]/30 transition-all group"
                    >
                      <div className="w-7 h-7 md:w-12 md:h-12 bg-green-100 rounded-lg md:rounded-xl flex items-center justify-center mb-1.5 md:mb-4 group-hover:bg-[#3D6B4A] transition-colors mx-auto md:mx-0">
                        <feature.icon className="w-3.5 h-3.5 md:w-6 md:h-6 text-[#2D5A3D] group-hover:text-white transition-colors" />
                      </div>
                      <h4 className="text-xs md:text-lg font-bold text-[#1E3A2F] mb-0.5 md:mb-2 text-center md:text-left line-clamp-2">{getContent(feature.titleKey, feature.defaultTitle)}</h4>
                      <p className="text-[#2D5A3D] text-[10px] md:text-sm text-center md:text-left line-clamp-2 hidden md:block">{getContent(feature.descKey, feature.defaultDesc)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTA Section - Transparent over hero */}
            <div className="relative py-16 md:py-24 px-4 sm:px-6 lg:px-8">
              <div className="max-w-4xl mx-auto text-center">
                <Heart className="w-12 h-12 text-white mx-auto mb-4 drop-shadow-lg" />
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 drop-shadow-lg">
                  {getContent("home_ready_title", "Ready to simplify your vendor planning?")}
                </h3>
                <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto drop-shadow">
                  {getContent("home_ready_subtitle", "Join hundreds of camp directors who use CampYid to find and book the best vendors for their camps.")}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-white text-[#1E3A2F] hover:bg-[#F5F0E6] rounded-full px-8 font-semibold"
                    onClick={() => navigate('/register')}
                    data-testid="camp-create-account-btn"
                  >
                    <UserPlus className="w-5 h-5 mr-2" />
                    {getContent("home_create_account_btn", "Create Free Account")}
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 font-semibold"
                    onClick={() => navigate('/search')}
                    data-testid="camp-browse-btn"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    {getContent("home_browse_first_btn", "Browse Vendors First")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Listing Quick View Drawer */}
      <Suspense fallback={null}>
        <ListingDrawer 
          listingSlug={selectedListingSlug}
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
      </Suspense>

      {/* SEO Content Footer — collapsible, keyword-rich, legitimate for Google */}
      {getContent("home_seo_enabled", "false") === "true" && (
      <div className="relative bg-[#F9F9F5] border-t border-stone-100" style={{ zIndex: 2 }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <details className="group">
            <summary className="cursor-pointer text-xs text-stone-400 hover:text-stone-500 transition-colors select-none list-none flex items-center gap-1">
              <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
              About CampYid
            </summary>
            <div className="mt-4 pb-4 space-y-3 text-sm text-[#2D5A3D]/80">
              <h2 className="text-lg font-semibold text-[#1E3A2F]">
                {getContent("home_seo_title", "The Camp Vendor Directory for Jewish Summer Camps")}
              </h2>
              <p>
                {getContent("home_seo_p1", "CampYid is the leading Jewish camp vendor directory where camp directors find trusted service providers. Whether you need kosher catering for visiting day, entertainment for color war, buses for field trips, or supplies for bunk activities — find the right camp vendor in minutes.")}
              </p>
              <p>
                {getContent("home_seo_p2", "Browse camp vendors by category: Jewish camp entertainment (performers, DJs, shows), kosher camp food & catering, transportation & bus services, trips & excursions, health & safety providers, office & admin solutions, promos & swag, supplies & equipment, and specialty programs.")}
              </p>
              <p>
                {getContent("home_seo_p3", "Camp directors use CampYid to save time finding kosher-friendly and camp-experienced vendors, organize their selections into lists, and plan their entire summer season. Vendors use CampYid to reach hundreds of Jewish summer camps searching for exactly the services they offer.")}
              </p>
            </div>
          </details>
        </div>
      </div>
      )}

      {/* Promo Subscribe Banner */}
      <PromoSubscribeBanner />
    </div>
  );
}
